import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import { checkAnswer, expectedAnswer } from "../Quiz/answer.ts";
import { cardFaces } from "../cardFaces.ts";

/*
 * Math Facts Soccer (ADR 0059): a penalty shootout where every kick is a fact.
 *
 * Pure, like dropGame.ts - no timers, no DOM, no React. The component feeds
 * `advance` animation-frame deltas and `kick` Enter presses, and renders
 * whatever comes back.
 */

/** Penalties in one match (§1). */
export const KICKS_PER_MATCH = 10;

/** "3... 2... 1..." before the first kick, so the meter is not already filling
 * while the child finds the box - the quiz's reason for its countdown (0003 §2). */
export const COUNTDOWN_MS = 3000;

/** A stalled frame costs at most this much of the meter (0053 §4). */
export const MAX_FRAME_MS = 250;

/** A kick this many goals into a run of consecutive goals is a hat trick. */
export const HAT_TRICK = 3;

export type Keeper = "rookie" | "pro" | "legend";

export const KEEPERS: Keeper[] = ["rookie", "pro", "legend"];

/** How long each keeper takes to get set (§3). */
export const KEEPER_READY_MS: Record<Keeper, number> = {
    rookie: 10_000,
    pro: 6_000,
    legend: 4_000,
};

/**
 * goal - right, before the keeper was set. saved - right, but too late.
 * wide - a wrong answer. skipped - an empty Enter.
 */
export type Outcome = "goal" | "saved" | "wide" | "skipped";

/** Left, middle or right of the goal, as the kicker sees it. */
export type Side = 0 | 1 | 2;

export interface Kick {
    /** 1-based; also keys the shot animation so each kick replays it. */
    number: number;
    /** The problem as shown, e.g. "7 + 8". */
    front: string;
    /** The correct answer, as text. */
    answer: string;
    /** What the child typed; empty for a skip. */
    typed: string;
    outcome: Outcome;
    /** Where the ball goes. */
    aim: Side;
    /** Where the keeper dives. */
    dive: Side;
    /** The third (sixth, ...) goal in a row. */
    hatTrick: boolean;
}

export interface SoccerState {
    phase: "countdown" | "playing" | "over";
    keeper: Keeper;
    countdownLeftMs: number;
    /** The fact on the spot now. Unchanged once the match is over. */
    problem: ProblemData;
    /** Play time this kick has been up, capped at the keeper's ready time. */
    kickElapsedMs: number;
    kicks: Kick[];
    goalStreak: number;
}

export function startMatch(keeper: Keeper, queue: QuizQueue): SoccerState {
    return {
        phase: "countdown",
        keeper,
        countdownLeftMs: COUNTDOWN_MS,
        problem: queue.next(),
        kickElapsedMs: 0,
        kicks: [],
        goalStreak: 0,
    };
}

/** Moves play time on. Never draws from the queue, so it is safe to call per frame. */
export function advance(state: SoccerState, dtMs: number): SoccerState {
    const dt = Math.min(Math.max(dtMs, 0), MAX_FRAME_MS);
    if (dt === 0 || state.phase === "over") {
        return state;
    }
    if (state.phase === "countdown") {
        const left = state.countdownLeftMs - dt;
        return left > 0
            ? { ...state, countdownLeftMs: left }
            : { ...state, phase: "playing", countdownLeftMs: 0, kickElapsedMs: 0 };
    }
    const ready = KEEPER_READY_MS[state.keeper];
    if (state.kickElapsedMs >= ready) {
        return state;
    }
    return { ...state, kickElapsedMs: Math.min(ready, state.kickElapsedMs + dt) };
}

/** How set the keeper is for this kick, 0 to 1. At 1 a right answer is saved. */
export function readiness(state: SoccerState): number {
    return Math.min(1, state.kickElapsedMs / KEEPER_READY_MS[state.keeper]);
}

export function keeperIsSet(state: SoccerState): boolean {
    return state.phase === "playing" && readiness(state) >= 1;
}

/**
 * Where the ball goes and where the keeper dives, derived from the kick rather
 * than drawn at random (§7) so the engine stays deterministic. A goal sends the
 * keeper the wrong way - alternating between the two other corners - and a save
 * sends them the right way. A skip rolls straight to a keeper who stays put.
 */
export function shotFor(answer: number, kickIndex: number, outcome: Outcome): { aim: Side; dive: Side } {
    if (outcome === "skipped") {
        return { aim: 1, dive: 1 };
    }
    const aim = (((answer + kickIndex) % 3) + 3) % 3 as Side;
    if (outcome === "goal") {
        return { aim, dive: ((aim + 1 + (kickIndex % 2)) % 3) as Side };
    }
    return { aim, dive: aim };
}

/**
 * Takes the penalty: marks what was typed, records the kick, and puts the next
 * fact on the spot in the same step - feedback never gates the next question
 * (0003 §1). The keeper meter decides only between goal and save; it never
 * moves the match on by itself (§2).
 */
export function kick(state: SoccerState, typed: string, queue: QuizQueue): SoccerState {
    if (state.phase !== "playing") {
        return state;
    }

    const faces = cardFaces(state.problem);
    const result = checkAnswer(state.problem, typed);
    const outcome: Outcome =
        result === "correct"
            ? keeperIsSet(state)
                ? "saved"
                : "goal"
            : result === "wrong"
            ? "wide"
            : "skipped";

    const goalStreak = outcome === "goal" ? state.goalStreak + 1 : 0;
    const kickIndex = state.kicks.length;
    const kicks = [
        ...state.kicks,
        {
            number: kickIndex + 1,
            front: faces.front,
            answer: faces.back,
            typed: typed.trim(),
            outcome,
            ...shotFor(expectedAnswer(state.problem), kickIndex, outcome),
            hatTrick: outcome === "goal" && goalStreak % HAT_TRICK === 0,
        },
    ];

    if (kicks.length >= KICKS_PER_MATCH) {
        return { ...state, phase: "over", kicks, goalStreak };
    }
    return { ...state, problem: queue.next(), kickElapsedMs: 0, kicks, goalStreak };
}

export function goalsOf(kicks: Kick[]): number {
    return kicks.filter((k) => k.outcome === "goal").length;
}

export function countOf(kicks: Kick[], outcome: Outcome): number {
    return kicks.filter((k) => k.outcome === outcome).length;
}
